import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import type { ExportDialogData, ExportDialogResult } from '../models';
import { ExportService } from '../export.service';
import { uniqueKeysFromRows, flattenObject, getByPath, normalizeForExport } from '../object-utils';

@Component({
  selector: 'app-export-dialog',
  templateUrl: './export-dialog.component.html',
  styleUrls: ['./export-dialog.component.scss'],
  providers: [ExportService],
})
export class ExportDialogComponent implements OnInit {
  title = 'Export';
  filename?: string;

  // checkboxes in Controls
  applyFilters = true;
  applySort = true;
  respectCurrentOrder = false;

  editingKey: string | null = null;
  tempHeader = '';
  exportCount = 0;

  // master columns model (all columns, with selected + header name)
  allColumns: Array<{ key: string; header: string; selected: boolean }> = [];

  // explicit order of selected columns (by key)
  columnOrderKeys: string[] = [];

  // available columns search
  availableFilter = '';

  // preview rows
  filteredPreview: any[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ExportDialogData,
    private ref: MatDialogRef<ExportDialogComponent, ExportDialogResult>,
    private exporter: ExportService
  ) { }

  ngOnInit(): void {
    this.title = this.data.title || 'Export';

    // Default filename: export_YYYYMMDD_HHMMSS (local time)
    if (!this.filename) this.filename = `export_${this.nowSlug()}`;

    const keys = this.data.presetColumns?.length
      ? this.data.presetColumns.map(c => c.key)
      : uniqueKeysFromRows(this.data.rows, !!this.data.flatten);

    const usableKeys = keys.filter(k => !(this.data.excludeKeys || []).includes(k));

    // build allColumns
    this.allColumns =
      (this.data.presetColumns?.map(c => ({
        key: c.key,
        header: c.header ?? this.headerFor(c.key),
        selected: true,
      }))) ??
      usableKeys
        .map(key => ({
          key,
          header: this.data.headerOverrides?.[key] ?? this.headerFor(key),
          selected: true, // start selected; user can remove from the right pane
        }))
        .sort((a, b) => a.header.localeCompare(b.header));

    // init order to current selected keys
    this.columnOrderKeys = this.allColumns.filter(c => c.selected).map(c => c.key);

    this.rebuildPreview();
  }

  private nowSlug(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const HH = pad(d.getHours());
    const MM = pad(d.getMinutes());
    const SS = pad(d.getSeconds());
    return `${yyyy}${mm}${dd}_${HH}${MM}${SS}`;
  }

  startEdit(col: { key: string; header: string }) {
    this.editingKey = col.key;
    this.tempHeader = col.header ?? '';
  }

  saveEdit(col: { key: string; header: string }) {
    const next = (this.tempHeader ?? '').trim();
    if (next) col.header = next;
    this.editingKey = null;
    this.tempHeader = '';
    this.rebuildPreview();
  }

  cancelEdit() {
    this.editingKey = null;
    this.tempHeader = '';
  }

  // selection toggle
  onToggleColumn(col: { key: string; selected: boolean }, selected: boolean) {
    col.selected = selected;
    if (selected) {
      if (!this.columnOrderKeys.includes(col.key)) {
        this.columnOrderKeys.push(col.key); // add newly selected to end
      }
    } else {
      this.columnOrderKeys = this.columnOrderKeys.filter(k => k !== col.key);
    }
    this.rebuildPreview();
  }

  // select all / clear all
  toggleAll(on: boolean) {
    this.allColumns.forEach(c => (c.selected = on));
    this.columnOrderKeys = on ? this.allColumns.map(c => c.key) : [];
    this.rebuildPreview();
  }

  // order helpers (for selected columns)
  orderIndex(key: string): number {
    return this.columnOrderKeys.indexOf(key);
  }

  moveSelected(col: { key: string }, delta: number) {
    const idx = this.orderIndex(col.key);
    if (idx < 0) return;
    const newIdx = Math.max(0, Math.min(this.columnOrderKeys.length - 1, idx + delta));
    if (newIdx === idx) return;
    const arr = this.columnOrderKeys;
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    this.rebuildPreview();
  }

  headerFor(key: string) {
    const last = key.split('.').pop() || key;
    return last;// last.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  friendlyHeaderFor(key: string) {
    const last = key.split('.').pop() || key;
    return last.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  // computed lists
  get selectedColumns() {
    const sel = this.allColumns.filter(c => c.selected);
    const order = new Map(this.columnOrderKeys.map((k, i) => [k, i]));
    return sel.sort((a, b) => (order.get(a.key) ?? 9999) - (order.get(b.key) ?? 9999));
  }

  get availableColumns() {
    const q = this.availableFilter.trim().toLowerCase();
    return this.allColumns
      .filter(c => !c.selected)
      .filter(c => !q || c.header.toLowerCase().includes(q) || c.key.toLowerCase().includes(q))
      .sort((a, b) => a.header.localeCompare(b.header));
  }

  // Selected first (in export order), then the rest (alpha by header)
  get columnsForList() {
    const order = new Map(this.columnOrderKeys.map((k, i) => [k, i]));
    const selected = this.allColumns
      .filter(c => c.selected)
      .sort((a, b) => (order.get(a.key)! - order.get(b.key)!));

    const unselected = this.allColumns
      .filter(c => !c.selected)
      .sort((a, b) => a.header.localeCompare(b.header));

    return [...selected, ...unselected];
  }

  trackByKey(_i: number, c: { key: string }) { return c.key; }

  // selection handlers

  addColumn(colKey: string) {
    const c = this.allColumns.find(x => x.key === colKey);
    if (!c) return;
    c.selected = true;
    if (!this.columnOrderKeys.includes(c.key)) this.columnOrderKeys.push(c.key);
    this.rebuildPreview();
  }

  removeColumn(colKey: string) {
    const c = this.allColumns.find(x => x.key === colKey);
    if (!c) return;
    c.selected = false;
    this.columnOrderKeys = this.columnOrderKeys.filter(k => k !== c.key);
    this.rebuildPreview();
  }

  addAll() {
    for (const c of this.allColumns) {
      if (!c.selected) {
        c.selected = true;
        if (!this.columnOrderKeys.includes(c.key)) this.columnOrderKeys.push(c.key);
      }
    }
    this.rebuildPreview();
  }

  removeAll() {
    for (const c of this.allColumns) c.selected = false;
    this.columnOrderKeys = [];
    this.rebuildPreview();
  }

  resetNames() {
    for (const c of this.allColumns) {
      c.header = this.data.headerOverrides?.[c.key] ?? this.headerFor(c.key);
    }
  }

  resetToFriendlyNames() {
    for (const c of this.allColumns) {
      c.header = this.data.headerOverrides?.[c.key] ?? this.friendlyHeaderFor(c.key);
    }
  }

  onRename(col: { key: string; header: string }) {
    // trigger change detection / live preview (no heavy work here)
  }

  onSelectedDrop(ev: CdkDragDrop<any>) {
    moveItemInArray(this.columnOrderKeys, ev.previousIndex, ev.currentIndex);
    this.rebuildPreview();
  }

  // preview plumbing
  rebuildPreview() {
    const rows = this.getRowsForExport();
    this.filteredPreview = rows.slice(0, 10);
    this.exportCount = rows.length;
  }

  private readRaw(row: any, key: string) {
    // Direct hit (covers accidental flattened objects with "a.b" keys)
    if (row && Object.prototype.hasOwnProperty.call(row, key)) {
      return row[key];
    }
    // Nested path
    return getByPath(row, key);
  }

  valueFor(row: any, key: string): string {
    const v = this.readRaw(row, key);
    const n = normalizeForExport(v);
    return typeof n === 'object' ? JSON.stringify(n) : String(n ?? '');
  }

  // === existing getRowsForExport logic (with restrictToInputRows support) ===
  private rowKeyFn = (r: any) =>
    typeof this.data.identityKey === 'function'
      ? this.data.identityKey(r)
      : this.data.identityKey
        ? r?.[this.data.identityKey as string]
        : r;

  private intersect(base: any[], candidate: any[]) {
    if (!Array.isArray(base) || !Array.isArray(candidate)) return base ?? [];
    const set = new Set(base.map(this.rowKeyFn));
    return candidate.filter(r => set.has(this.rowKeyFn(r)));
  }

  private orderLike(reference: any[], list: any[]) {
    const set = new Set(list.map(this.rowKeyFn));
    return reference.filter(r => set.has(this.rowKeyFn(r)));
  }


  getRowsForExport(): any[] {
    const base = this.data.rows ?? [];
    const ds: any = this.data.dataSource;

    // if true → never go beyond `base`
    const restrict = !!this.data.restrictToInputRows;

    console.log('[export] ---- getRowsForExport ----');
    console.log('[export] base.length (from caller):', base.length);
    console.log('[export] has datasource:', !!ds);
    console.log('[export] applyFilters:', this.applyFilters);
    console.log('[export] applySort:', this.applySort);
    console.log('[export] restrictToInputRows:', restrict);
    console.log('[export] identityKey:', this.data.identityKey);

    // start from caller rows
    let working = base;

    // 1) RESTRICTED PATH (Resolution Center style)
    if (restrict) {
      console.log('[export] RESTRICTED MODE: will NOT expand beyond base');

      // apply filters only to base
      if (this.applyFilters && ds?.filterPredicate) {
        const filterStr = ds.filter ?? '';
        const pred = ds.filterPredicate.bind(ds);
        console.log('[export] (restricted) applying table filter with filterStr:', filterStr);
        working = base.filter((row: any, idx: number) => {
          const keep = pred(row, filterStr);
          if (!keep) {
            console.log('[export] (restricted) filtered OUT row @', idx, 'id=', this._safeKey(row));
          }
          return keep;
        });
        console.log('[export] (restricted) after filter, working.length:', working.length);
      } else {
        console.log('[export] (restricted) skip filter, working.length:', working.length);
      }

      // apply sort only to current working
      if (this.applySort && ds) {
        console.log('[export] (restricted) applying sort based on table order');
        working = this._sortLikeTable(ds, working);
        console.log('[export] (restricted) after sort, working.length:', working.length);
      } else {
        console.log('[export] (restricted) skip sort');
      }

      if (!working.length) {
        console.log('[export] (restricted) WARNING: final list empty');
      }
      console.log('[export] ---- /getRowsForExport (restricted) ----');
      return working;
    }

    // 2) UNRESTRICTED PATH (Main grid style)
    // here we ARE allowed to expand to the full datasource when filters are off
    let sourceForFiltering: any[] = base;

    if (this.applyFilters && ds?.filterPredicate) {
      // user wants current filters → filter only what caller sent
      const filterStr = ds.filter ?? '';
      const pred = ds.filterPredicate.bind(ds);
      console.log('[export] (unrestricted) applying filter to base with filterStr:', filterStr);
      working = base.filter((row: any) => pred(row, filterStr));
      console.log('[export] (unrestricted) after filter, working.length:', working.length);
    } else {
      // user turned filters OFF → we can use the FULL table
      if (ds?.data?.length) {
        console.log('[export] (unrestricted) filters OFF → using FULL datasource of', ds.data.length);
        working = ds.data.slice(); // <-- 133 rows here
      } else {
        console.log('[export] (unrestricted) filters OFF but no datasource, using base');
        working = base;
      }
    }

    // now apply sort (still only reordering what we currently have)
    if (this.applySort && ds) {
      console.log('[export] (unrestricted) applying sort to current working set, length:', working.length);
      working = this._sortLikeTable(ds, working);
      console.log('[export] (unrestricted) after sort, working.length:', working.length);
    } else {
      console.log('[export] (unrestricted) skip sort');
    }

    if (!working.length) {
      console.log('[export] (unrestricted) WARNING: final list empty');
    } else {
      console.log('[export] (unrestricted) final list length:', working.length);
    }

    console.log('[export] ---- /getRowsForExport (unrestricted) ----');
    return working;
  }

  // helper to sort a list to match the table order
  private _sortLikeTable(ds: any, list: any[]): any[] {
    const tableOrder: any[] = ds.data ?? [];
    console.log('[export] _sortLikeTable: tableOrder.length =', tableOrder.length);

    const keyFn =
      typeof this.data.identityKey === 'function'
        ? this.data.identityKey
        : (r: any) =>
          this.data.identityKey ? r?.[this.data.identityKey as string] : r;

    const indexByKey = new Map<any, number>();
    tableOrder.forEach((r, idx) => {
      const k = keyFn(r);
      if (k !== undefined && k !== null && !indexByKey.has(k)) {
        indexByKey.set(k, idx);
      }
    });
    console.log('[export] _sortLikeTable: indexByKey.size =', indexByKey.size);

    return [...list].sort((a, b) => {
      const ka = keyFn(a);
      const kb = keyFn(b);
      const ia = indexByKey.has(ka) ? indexByKey.get(ka)! : Number.MAX_SAFE_INTEGER;
      const ib = indexByKey.has(kb) ? indexByKey.get(kb)! : Number.MAX_SAFE_INTEGER;
      return ia - ib;
    });
  }

  private _safeKey(row: any): any {
    if (!row) return row;
    const k =
      typeof this.data.identityKey === 'function'
        ? this.data.identityKey(row)
        : this.data.identityKey
          ? row?.[this.data.identityKey as string]
          : row;
    return k;
  }








  // export
  export() {
    const fname = this.safeName(this.filename || `export_${this.nowSlug()}`);

    const rows = this.getRowsForExport().map(r => {
      const out: Record<string, any> = {};
      for (const c of this.selectedColumns) {
        const raw = this.data.accessors?.[c.key]
          ? this.data.accessors[c.key](r)                           // accessor on original row
          : this.readRaw(r, c.key);                                 // robust read
        out[c.key] = normalizeForExport(raw);                    // booleans → Yes/No
      }
      return out;
    });

    this.exporter.exportXlsx({
      filename: fname,
      rows,
      columns: this.selectedColumns.map(c => ({ key: c.key, header: c.header })),
      accessors: this.data.accessors
    });

    this.ref.close({ exported: true });
  }

  cancel() {
    this.ref.close({ exported: false });
  }

  previewChanged(): void {
    this.rebuildPreview();
  }

  private safeName(name: string) {
    return name.trim().replace(/[\\/:*?"<>|]+/g, '_').replace(/\.+$/, '');
  }
}
